import { faCheckCircle, faVial } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Modal, Table } from 'react-bootstrap';
import { parseAsUTC } from '../../../../utils/time';
import './CitationDetailsModal.scss';
import { ScanMulti } from './models';
import { LabsModal } from '../../Labs';
import { convertScanMultiLabsToLabArray } from '../../../../utils/lab-data-adapter';
import { Lab } from '../../../../amplify/api-gateway/query';

// Mock ScanMulti data structure for demo
const mockScanMulti = {
    citation_mapping: {
        "1": { data_type: "patient_demographics", item_key: "demographics", metadata: { source: "EHR", timestamp: "2024-01-15T10:30:00Z" } },
        "2": { data_type: "lab_result", item_key: "CBC", metadata: { lab_name: "Complete Blood Count", collection_time: "2024-01-15T08:00:00Z" } },
        "3": { data_type: "imaging", item_key: "chest_xray", metadata: { study_type: "Chest X-Ray", performed_date: "2024-01-15T09:15:00Z" } },
        "4": { data_type: "encounter", item_key: "emergency_visit", metadata: { visit_type: "Emergency Department", arrival_time: "2024-01-15T07:45:00Z" } },
        "5": { data_type: "medication", item_key: "hospital_meds", metadata: { medication_count: 3 } },
        "6": { data_type: "order", item_key: "clinical_orders", metadata: { order_session: "morning_rounds" } },
        "7": { data_type: "vital_1min", item_key: "continuous_monitoring", metadata: { monitor_type: "Bedside" } },
        "8": { data_type: "home_medication", item_key: "outpatient_meds", metadata: { reconciliation_date: "2024-01-15T07:45:00Z" } },
        "9": { data_type: "diagnosis", item_key: "pmh_diabetes", metadata: { icd10: "E11.9" } },
        "10": { data_type: "echo", item_key: "tte_study", metadata: { study_id: "ECHO-2024-001" } },
        "11": { data_type: "ecg", item_key: "12lead_ecg", metadata: { ecg_id: "ECG-2024-001" } },
        "12": { data_type: "vital_signs_summary", item_key: "12345_summary", metadata: { csn: "12345" } },
        "13": { data_type: "rhythm_summary", item_key: "continuous_ecg_summary", metadata: {} },
        "14": { data_type: "rhythm_episode", item_key: "afib_episode_1", metadata: {} },
        "15": { data_type: "risk_prediction", item_key: "mortality_30d", metadata: { model_version: "v2.1" } },
        "16": { data_type: "risk_feature", item_key: "age_feature", metadata: { feature_rank: 1 } },
        "17": { data_type: "clinical_note", item_key: "ed_note", metadata: { author: "Dr. Smith" } },
        "18": { data_type: "reason_for_visit", item_key: "chief_complaint", metadata: {} }
    },
    
    // Patient Demographics
    patient: { 
        _citation_id: "1", 
        age: "65", 
        gender: "Male", 
        race: "Caucasian", 
        ethnicity: "Non-Hispanic" 
    },
    
    // Laboratory Results - organized by test type
    labs: {
        "CBC": {
            component_units: "cells/µL",
            results: [
                { _citation_id: "2", result_time: "2024-01-15T08:00:00Z", component_value: "7500", component_abnormal: "Normal", display_name: "White Blood Cell Count" },
                { _citation_id: "2", result_time: "2024-01-15T08:00:00Z", component_value: "4.2", component_abnormal: "Normal", display_name: "Red Blood Cell Count" },
                { _citation_id: "2", result_time: "2024-01-15T08:00:00Z", component_value: "13.5", component_abnormal: "Normal", display_name: "Hemoglobin" },
                { _citation_id: "2", result_time: "2024-01-15T08:00:00Z", component_value: "40", component_abnormal: "Normal", display_name: "Hematocrit" },
                { _citation_id: "2", result_time: "2024-01-15T08:00:00Z", component_value: "250000", component_abnormal: "Normal", display_name: "Platelet Count" }
            ]
        },
        "BMP": {
            component_units: "mmol/L",
            results: [
                { _citation_id: "19", result_time: "2024-01-15T08:00:00Z", component_value: "140", component_abnormal: "Normal", display_name: "Sodium" },
                { _citation_id: "19", result_time: "2024-01-15T08:00:00Z", component_value: "4.0", component_abnormal: "Normal", display_name: "Potassium" },
                { _citation_id: "19", result_time: "2024-01-15T08:00:00Z", component_value: "1.0", component_abnormal: "High", display_name: "Creatinine" }
            ]
        }
    },
    
    // Imaging Studies
    imaging: [
        { 
            _citation_id: "3", 
            study: "Chest X-Ray", 
            result_time: "2024-01-15T09:15:00Z", 
            impression: "Clear lungs, no acute findings. Normal cardiac silhouette.", 
            days_ago: 0, 
            regions: ["chest"], 
            modality: "XR" 
        }
    ],
    
    // Clinical Encounters/Visits
    visits: [
        { 
            _citation_id: "4", 
            csn: "12345", 
            arrival_date: "2024-01-15T07:45:00Z", 
            payor_class: "Commercial", 
            means_of_arrival_cat: "Walk-in", 
            cc: "Chest pain", 
            triage_acuity: "3", 
            dispo_class_adt: "Discharge", 
            dx_name: ["Chest pain, unspecified", "Hypertension"], 
            ed_los: 180, 
            admit_service_adt: "", 
            level_of_care_adt: null 
        }
    ],
    
    // Hospital Medications (administered)
    meds: [
        { _citation_id: "5", med_name: "Lisinopril 10mg", first_admin_time: "2024-01-15T12:00:00Z" },
        { _citation_id: "5", med_name: "Aspirin 81mg", first_admin_time: "2024-01-15T12:00:00Z" },
        { _citation_id: "5", med_name: "Nitroglycerin 0.4mg SL", first_admin_time: "2024-01-15T08:30:00Z" }
    ],
    
    // Clinical Orders
    orders: [
        { _citation_id: "6", name: "Chest X-Ray", type: "Imaging", order_time: "2024-01-15T08:30:00Z" },
        { _citation_id: "6", name: "CBC with Differential", type: "Laboratory", order_time: "2024-01-15T08:30:00Z" },
        { _citation_id: "6", name: "Basic Metabolic Panel", type: "Laboratory", order_time: "2024-01-15T08:30:00Z" },
        { _citation_id: "6", name: "Troponin I", type: "Laboratory", order_time: "2024-01-15T08:30:00Z" },
        { _citation_id: "6", name: "ECG 12-Lead", type: "Cardiology", order_time: "2024-01-15T08:30:00Z" }
    ],
    
    // High-frequency Vital Signs (1-minute intervals)
    vs_1min: [
        { _citation_id: "7", numeric_time: "2024-01-15T08:00:00Z", hr: 72, rr: 16, spo2: 98, nbps: 120, nbpd: 80, perf: 2.1 },
        { _citation_id: "7", numeric_time: "2024-01-15T08:01:00Z", hr: 74, rr: 16, spo2: 98, nbps: 122, nbpd: 82, perf: 2.2 },
        { _citation_id: "7", numeric_time: "2024-01-15T08:02:00Z", hr: 73, rr: 18, spo2: 97, nbps: 118, nbpd: 78, perf: 2.0 },
        { _citation_id: "7", numeric_time: "2024-01-15T08:03:00Z", hr: 75, rr: 17, spo2: 98, nbps: 121, nbpd: 81, perf: 2.1 },
        { _citation_id: "7", numeric_time: "2024-01-15T08:04:00Z", hr: 71, rr: 16, spo2: 99, nbps: 119, nbpd: 79, perf: 2.3 }
    ],
    
    // Home Medications
    home_meds: [
        { _citation_id: "8", med_subclass: "Metformin 1000mg BID", order_date: "2023-12-01T00:00:00Z" },
        { _citation_id: "8", med_subclass: "Atorvastatin 40mg Daily", order_date: "2023-11-15T00:00:00Z" },
        { _citation_id: "8", med_subclass: "Lisinopril 20mg Daily", order_date: "2023-10-01T00:00:00Z" }
    ],
    
    // Past Medical History / Diagnoses
    pmh: [
        { _citation_id: "9", desc10: "Type 2 Diabetes Mellitus without complications", noted_date: "2020-03-15T00:00:00Z" }
    ],
    
    // Echocardiogram Studies
    echo: [
        { 
            _citation_id: "10", 
            display_name: "Transthoracic Echocardiogram", 
            result_time: "2024-01-14T14:30:00Z",
            narrative: "Normal left ventricular size and systolic function. LVEF 55-60%. No significant valvular abnormalities.",
            lvef_values: { value: 57, unit: "%" },
            rvsp_values: { value: 28, unit: "mmHg" }
        }
    ],
    
    // 12-Lead ECG
    ecg_12L: [
        {
            _citation_id: "11",
            result_time: "2024-01-15T08:15:00Z",
            impression: "Normal sinus rhythm. Normal ECG.",
            rhythm: "Sinus rhythm",
            measurements: {
                HR: 72,
                PR: 160,
                QRS: 90,
                QTc: 420,
                P_axis: 45,
                QRS_axis: 60,
                T_axis: 50
            }
        }
    ],
    
    // Vital Signs Summary by CSN
    vs_stats_by_csn: {
        "12345": {
            _citation_id: "12",
            dispo_class: "Discharge",
            start_time: "2024-01-15T08:00:00Z",
            end_time: "2024-01-15T11:00:00Z",
            measures: {
                "Heart Rate": {
                    min: 68,
                    max: 85,
                    mean: 74,
                    median: 73,
                    std: 4.2
                },
                "SpO2": {
                    min: 96,
                    max: 100,
                    mean: 98,
                    median: 98,
                    std: 1.1
                },
                "Systolic BP": {
                    min: 115,
                    max: 135,
                    mean: 123,
                    median: 122,
                    std: 5.8
                }
            }
        }
    },
    
    // Continuous ECG Rhythm Summary Statistics
    ecg_continuous_stats: {
        _citation_id: "13",
        total_monitoring_time_seconds: 10800, // 3 hours
        total_rhythm_episodes: 45,
        unique_rhythms_detected: 3,
        rhythm_changes: 12,
        rhythm_burdens: {
            "Sinus rhythm": 0.85,
            "Atrial fibrillation": 0.12,
            "Sinus tachycardia": 0.03
        },
        rhythm_episode_counts: {
            "Sinus rhythm": 30,
            "Atrial fibrillation": 12,
            "Sinus tachycardia": 3
        },
        rhythm_durations: {
            "Atrial fibrillation": {
                min: 45,
                max: 320,
                mean: 108,
                median: 95,
                total: 1296
            },
            "Sinus tachycardia": {
                min: 60,
                max: 180,
                mean: 108,
                median: 120,
                total: 324
            }
        }
    },
    
    // Individual Rhythm Episodes (granular data)
    ecg_continuous_granular: [
        {
            _citation_id: "14",
            segment_rhythm: "Atrial fibrillation",
            segment_start: "2024-01-15T09:30:00Z",
            segment_end: "2024-01-15T09:35:00Z",
            segment_duration: 300,
            hr_min: 85,
            hr_mean: 105,
            hr_max: 130,
            hr_n_points: 300
        }
    ],
    
    // Risk Predictions
    predictions: {
        "mortality_30d": {
            _citation_id: "15",
            outcome: "30-Day Mortality",
            prediction: {
                probability: 0.05,
                calibrated_prediction: 0.048,
                percentile: 65.3
            },
            top_features: [
                { _citation_id: "16", name: "Age", importance: 0.23 },
                { _citation_id: "16", name: "Creatinine", importance: 0.18 },
                { _citation_id: "16", name: "Heart Rate Variability", importance: 0.15 }
            ]
        }
    },
    
    // Clinical Notes
    notes: {
        "ED Provider Note": [
            {
                _citation_id: "17",
                note_type: "ED Provider Note",
                note_date: "2024-01-15T10:30:00Z",
                note_service: "Emergency Medicine",
                keywords: ["chest pain", "cardiac workup", "discharge"],
                note_text: "65 year old male with chest pain. Cardiac workup negative. Discharged home with cardiology follow-up."
            }
        ]
    },
    
    // Reason for Visit
    reasons_for_visit: {
        _citation_id: "18",
        reason: "Chest pain"
    }
};

interface CitationDetailsModalProps {
    citationNumber?: string;
    scanMulti?: ScanMulti;
    show: boolean;
    setShow: (show: boolean) => void;
    patientVisit?: any;
}

export const CitationDetailsModal = ({ citationNumber, scanMulti, show, setShow, patientVisit }: CitationDetailsModalProps) => {
    const [showLabsModal, setShowLabsModal] = useState(false);
    const [transformedLabs, setTransformedLabs] = useState<Lab[][]>([]);

    // Transform labs when scanMulti changes
    useEffect(() => {
        if (scanMulti?.labs) {
            setTransformedLabs(convertScanMultiLabsToLabArray(scanMulti.labs));
        }
    }, [scanMulti]);

    const citationData = useMemo(() => {
        console.log("CitationDetailsModal", scanMulti, citationNumber);
        if (citationNumber === undefined || !scanMulti) {
            return;
        }

        const mapping = scanMulti.citation_mapping[citationNumber];
        if (!mapping) return null;

        const { data_type, item_key, metadata } = mapping;
        
        let sourceData: any = null;
        let displayData: any = {};
        let multipleEntries: any[] = [];

        switch (data_type) {
            case 'patient':
            case 'patient_demographics':
                sourceData = scanMulti.patient;
                displayData = {
                    'Age': sourceData?.age,
                    'Gender': sourceData?.gender,
                    'Race': sourceData?.race,
                    'Ethnicity': sourceData?.ethnicity
                };
                break;
            
            case 'labs':
            case 'lab':
            case 'lab_result':
                // Search through ALL lab tests to find results matching this citation ID
                const allLabResults: any[] = [];
                let labUnits = '';
                
                if (scanMulti.labs) {
                    Object.entries(scanMulti.labs).forEach(([labName, labData]: [string, any]) => {
                        if (labData?.results) {
                            const matchingResults = labData.results.filter((result: any) => result._citation_id === citationNumber);
                            if (matchingResults.length > 0) {
                                allLabResults.push(...matchingResults);
                                // Use the units from the first matching lab (they should be consistent for the same citation)
                                if (!labUnits && labData.component_units) {
                                    labUnits = labData.component_units;
                                }
                            }
                        }
                    });
                }

                if (allLabResults.length > 0) {
                    multipleEntries = allLabResults;
                    const firstResult = allLabResults[0];
                    displayData = {
                        'Result Time': parseAsUTC(firstResult.result_time).toLocaleString(),
                        'Total Components': allLabResults.length,
                    };
                }
                break;

            case 'imaging':
                sourceData = scanMulti.imaging?.find(img => img._citation_id === citationNumber);
                if (sourceData) {
                    displayData = {
                        'Study Type': sourceData.study,
                        'Modality': sourceData.modality,
                        'Regions': sourceData.regions?.join(', '),
                        'Impression': sourceData.impression,
                        'Result Time': parseAsUTC(sourceData.result_time).toLocaleString(),
                        'Days Ago': sourceData.days_ago
                    };
                }
                break;
            
            case 'visits':
            case 'encounter':
                sourceData = scanMulti.visits?.find(visit => visit._citation_id === citationNumber);
                if (sourceData) {
                    displayData = {
                        'CSN': sourceData.csn,
                        'Chief Complaint': sourceData.cc,
                        'Arrival Date': parseAsUTC(sourceData.arrival_date).toLocaleString(),
                        'Triage Acuity': sourceData.triage_acuity,
                        'Payor Class': sourceData.payor_class,
                        'Disposition': sourceData.dispo_class_adt,
                        'Diagnoses': sourceData.dx_name?.join('; ')
                    };
                }
                break;
            
            case 'meds':
            case 'medication':
                // Find all medications that match this citation ID
                const allMeds = scanMulti.meds?.filter(med => med._citation_id === citationNumber) || [];
                
                if (allMeds.length > 0) {
                    multipleEntries = allMeds;
                    const firstMed = allMeds[0];
                    
                    displayData = {
                        'First Admin Time': firstMed.first_admin_time ? parseAsUTC(firstMed.first_admin_time).toLocaleString() : 'Not administered',
                        'Total Medications': allMeds.length,
                        'Source': 'Hospital Administration'
                    };
                }
                break;

            case 'orders':
            case 'order':
                // Get all orders that match this citation ID
                multipleEntries = scanMulti.orders?.filter(order => order._citation_id === citationNumber) || [];
                if (multipleEntries.length > 0) {
                    displayData = {
                        'Total Orders': multipleEntries.length,
                        'Order Types': [...new Set(multipleEntries.map(o => o.type))].join(', ')
                    };
                }
                break;

            case 'vital_1min':
                // Get all vital signs that match this citation ID
                multipleEntries = scanMulti.vs_1min?.filter(vs => vs._citation_id === citationNumber) || [];
                if (multipleEntries.length > 0) {
                    const firstEntry = multipleEntries[0];
                    const lastEntry = multipleEntries[multipleEntries.length - 1];
                    displayData = {
                        'Monitoring Period': `${parseAsUTC(firstEntry.numeric_time).toLocaleString()} - ${parseAsUTC(lastEntry.numeric_time).toLocaleString()}`,
                        'Total Measurements': multipleEntries.length,
                        'Frequency': 'Every minute',
                        'Parameters Monitored': 'HR, RR, SpO2, BP, Perfusion Index'
                    };
                }
                break;

            case 'diagnosis':
                sourceData = scanMulti.pmh?.find(dx => dx._citation_id === citationNumber);
                if (sourceData) {
                    displayData = {
                        'Description': sourceData.desc10,
                        'Noted Date': parseAsUTC(sourceData.noted_date).toLocaleString()
                    };
                }
                break;

            case 'home_med':
            case 'home_meds':
            case 'home_medication':
                // Find all home medications that match this citation ID
                const allHomeMeds = scanMulti.home_meds?.filter(med => med._citation_id === citationNumber) || [];
                
                if (allHomeMeds.length > 0) {
                    multipleEntries = allHomeMeds;
                    const firstMed = allHomeMeds[0];
                    displayData = {
                        'Order Date': parseAsUTC(firstMed.order_date).toLocaleString(),
                        'Total Medications': allHomeMeds.length,
                        'Source': 'Home Medication List'
                    };
                }
                break;

            case 'reason_for_visit':
            case 'reasons_for_visit':
                sourceData = scanMulti.reasons_for_visit;
                if (sourceData) {
                    displayData = {
                        'Reason': sourceData.reason
                    };
                }
                break;

            case 'echo':
                sourceData = scanMulti.echo?.find(echo => echo._citation_id === citationNumber);
                if (sourceData) {
                    displayData = {
                        'Study': sourceData.display_name,
                        'Result Time': parseAsUTC(sourceData.result_time).toLocaleString(),
                        'Narrative': sourceData.narrative,
                        'LVEF Data': JSON.stringify(sourceData.lvef_values),
                        'RVSP Data': JSON.stringify(sourceData.rvsp_values)
                    };
                }
                break;

            case 'ecg':
                sourceData = scanMulti.ecg_12L?.find(ecg => ecg._citation_id === citationNumber);
                if (sourceData) {
                    displayData = {
                        'Result Time': parseAsUTC(sourceData.result_time).toLocaleString(),
                        'Impression': sourceData.impression,
                        'Rhythm': sourceData.rhythm,
                        'HR': sourceData.measurements?.HR,
                        'PR': sourceData.measurements?.PR,
                        'QRS': sourceData.measurements?.QRS,
                        'QTc': sourceData.measurements?.QTc,
                        'P axis': sourceData.measurements?.P_axis,
                        'QRS axis': sourceData.measurements?.QRS_axis,
                        'T axis': sourceData.measurements?.T_axis,
                    };
                }
                break;

            case 'vital_signs_summary':
                sourceData = scanMulti.vs_stats_by_csn?.[item_key.split("_")[0]];
                if (sourceData) {
                    displayData = {
                        'Disposition': sourceData.dispo_class,
                        'Start Time': parseAsUTC(sourceData.start_time).toLocaleString(),
                        'End Time': parseAsUTC(sourceData.end_time).toLocaleString(),
                    };
                    for (let v of Object.keys(sourceData.measures)) {
                        let displayText = "";
                        for (let k of Object.keys(sourceData.measures[v])) {
                            displayText += `${k}: ${sourceData.measures[v][k]}\n`;
                        }
                        displayData[v] = displayText + "\n--See Monitor tab for trend details--";
                    }
                }
                break;

            case 'rhythm_summary':
                sourceData = scanMulti.ecg_continuous_stats;
                if (sourceData) {
                    displayData = {
                        'Citation ID': sourceData._citation_id,
                        'Total Monitoring Time (s)': sourceData.total_monitoring_time_seconds,
                        'Total Rhythm Episodes': sourceData.total_rhythm_episodes,
                        'Unique Rhythms Detected': sourceData.unique_rhythms_detected,
                        'Rhythm Changes': sourceData.rhythm_changes,
                    };

                    // Rhythm Burdens
                    if (sourceData.rhythm_burdens) {
                        let burdenText = "";
                        for (let rhythm of Object.keys(sourceData.rhythm_burdens)) {
                            burdenText += `${rhythm}: ${(sourceData.rhythm_burdens[rhythm] * 100).toFixed(2)}%\n`;
                        }
                        displayData['Rhythm Burdens'] = burdenText.trim();
                    }

                    // Rhythm Episode Counts
                    if (sourceData.rhythm_episode_counts) {
                        let countsText = "";
                        for (let rhythm of Object.keys(sourceData.rhythm_episode_counts)) {
                            countsText += `${rhythm}: ${sourceData.rhythm_episode_counts[rhythm]}\n`;
                        }
                        displayData['Rhythm Episode Counts'] = countsText.trim();
                    }

                    // Rhythm Durations
                    if (sourceData.rhythm_durations) {
                        for (let rhythm of Object.keys(sourceData.rhythm_durations)) {
                            let details = sourceData.rhythm_durations[rhythm];
                            let durationText = "";
                            for (let k of Object.keys(details)) {
                                durationText += `${k}: ${details[k]}\n`;
                            }
                            displayData[`${rhythm} Durations`] = durationText.trim() + "\n--See Monitor tab for trend details--";
                        }
                    }
                }
                break;

            case 'rhythm_episode':
                sourceData = scanMulti.ecg_continuous_granular?.find(rhythm => rhythm._citation_id === citationNumber);
                if (sourceData) {
                    displayData = {
                        'Rhythm': sourceData.segment_rhythm,
                        'Start Time': parseAsUTC(sourceData.segment_start).toLocaleString(),
                        'End Time': parseAsUTC(sourceData.segment_end).toLocaleString(),
                        'Duration (seconds)': sourceData.segment_duration,
                        'HR Min': sourceData.hr_min,
                        'HR Mean': sourceData.hr_mean,
                        'HR Max': sourceData.hr_max,
                        'HR Data Points': sourceData.hr_n_points
                    };
                }
                break;

            case 'risk_prediction':
                try {
                    // @ts-ignore
                    sourceData = scanMulti.predictions?.[item_key];
                } catch (e) {
                    console.error("Could not get risk predictions")
                }
                if (sourceData) {
                    displayData = {
                        'Outcome': sourceData.outcome,
                        'Probability': `${(sourceData.prediction?.probability * 100).toFixed(1)}%`,
                        'Calibrated Prediction': `${(sourceData.prediction?.calibrated_prediction * 100).toFixed(1)}%`,
                        'Percentile': `${sourceData.prediction?.percentile.toFixed(1)}`,
                        'Top Features': sourceData.top_features?.length || 0
                    };
                }
                break;

            case 'risk_feature':
                // Risk features are nested within predictions, so we need to find the parent
                const allPredictions = scanMulti.predictions || {};
                for (const [predKey, prediction] of Object.entries(allPredictions)) {
                    const feature = prediction.top_features?.find((f: any) => f._citation_id === citationNumber);
                    if (feature) {
                        sourceData = feature;
                        displayData = {
                            'Feature Name': feature.name,
                            'Importance': feature.importance?.toFixed(4),
                            'Model': predKey
                        };
                        break;
                    }
                }
                break;

            case 'note':
            case 'notes':
            case 'clinical_note':
                // Notes are nested within note types, so we need to find the right one
                const allNotes = scanMulti.notes || {};
                for (const [noteType, notes] of Object.entries(allNotes)) {
                    const note = notes.find(n => n._citation_id === citationNumber);
                    if (note) {
                        sourceData = note;
                        displayData = {
                            'Note Type': note.note_type,
                            'Note Date': parseAsUTC(note.note_date).toLocaleString(),
                            'Service': note.note_service,
                            'Keywords': note.keywords?.join(', ') || 'None',
                            'Text': note.note_text
                        };
                        break;
                    }
                }
                break;
        }

        return {
            data_type,
            item_key,
            metadata,
            displayData,
            sourceData,
            multipleEntries
        };
    }, [citationNumber, scanMulti]);

    const formatKey = (key: string) => {
        return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getBadgeVariant = (dataType: string) => {
        const variants: { [key: string]: string } = {
            'patient': 'primary',
            'patient_demographics': 'primary',
            'labs': 'success',
            'lab': 'success',
            'imaging': 'info',
            'visits': 'warning',
            'encounter': 'warning',
            'meds': 'secondary',
            'medication': 'secondary',
            'orders': 'danger',
            'order': 'danger',
            'notes': 'dark',
            'note': 'dark',
            'diagnosis': 'danger',
            'home_med': 'light',
            'home_meds': 'light',
            'home_medication': 'light',
            'reason_for_visit': 'info',
            'reasons_for_visit': 'info',
            'echo': 'primary',
            'ecg': 'warning',
            'vital_signs_summary': 'success',
            'vital_1min': 'success',
            'rhythm_episode': 'warning',
            'risk_prediction': 'danger',
            'risk_feature': 'secondary'
        };
        return variants[dataType] || 'secondary';
    };

    const renderMultipleEntries = () => {
        if (!citationData?.multipleEntries || citationData.multipleEntries.length === 0) {
            return null;
        }

        const { data_type, multipleEntries } = citationData;

        if (data_type === 'labs' || data_type === 'lab' || data_type === 'lab_result') {
            return (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="citation-section-title mb-0">Laboratory Results</h6>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                                setShowLabsModal(true);
                                setShow(false);
                            }}
                        >
                            <FontAwesomeIcon icon={faVial} className="me-1" />
                            View All Labs
                        </Button>
                    </div>
                    <Table className="citation-table" striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Component</th>
                                <th>Value</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {multipleEntries.map((result, index) => (
                                <tr key={index}>
                                    <td>{result.display_name}</td>
                                    <td><strong>{result.component_value}</strong></td>
                                    <td>
                                        <Badge bg={(result.component_abnormal ?? '').toLowerCase() === 'normal' ? 'success' : 'warning'}>
                                            {result.component_abnormal}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            );
        }

        if (data_type === 'orders' || data_type === 'order') {
            return (
                <div>
                    <h6 className="citation-section-title">Orders Placed</h6>
                    <Table className="citation-table" striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Order Name</th>
                                <th>Order Time</th>
                                <th>Type</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {multipleEntries.map((order, index) => (
                                <tr key={index}>
                                    <td>{order.name}</td>
                                    <td>{parseAsUTC(order.order_time).toLocaleString()}</td>
                                    <td>
                                        <Badge bg="secondary">
                                            {order.type}
                                        </Badge>
                                    </td>
                                    <td>Active</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            );
        }

        if (data_type === 'meds' || data_type === 'medication') {
            return (
                <div>
                    <h6 className="citation-section-title">Hospital Medications</h6>
                    <Table className="citation-table" striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Medication</th>
                                <th>Admin Time</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {multipleEntries.map((med, index) => (
                                <tr key={index}>
                                    <td><strong>{med.med_name}</strong></td>
                                    <td>
                                        {med.first_admin_time ? 
                                            parseAsUTC(med.first_admin_time).toLocaleString() : 
                                            <span className="text-muted">Not administered</span>
                                        }
                                    </td>
                                    <td>
                                        <Badge bg={med.first_admin_time ? 'success' : 'warning'}>
                                            {med.first_admin_time ? 'Administered' : 'Pending'}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            );
        }

        if (data_type === 'vital_1min') {
            return (
                <div>
                    <h6 className="citation-section-title">Vital Signs Monitoring</h6>
                    <Table className="citation-table" striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>HR</th>
                                <th>RR</th>
                                <th>SpO2</th>
                                <th>BP</th>
                                <th>PI</th>
                            </tr>
                        </thead>
                        <tbody>
                            {multipleEntries.slice(0, 10).map((vital, index) => (
                                <tr key={index}>
                                    <td>{parseAsUTC(vital.numeric_time).toLocaleTimeString()}</td>
                                    <td>{vital.hr}</td>
                                    <td>{vital.rr}</td>
                                    <td>{vital.spo2}%</td>
                                    <td>{vital.nbps}/{vital.nbpd}</td>
                                    <td>{vital.perf}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {multipleEntries.length > 10 && (
                        <div className="text-muted text-center mt-2">
                            <small>Showing first 10 of {multipleEntries.length} measurements</small>
                        </div>
                    )}
                </div>
            );
        }

        if (data_type === 'home_meds' || data_type === 'home_med' || data_type === 'home_medication') {
            return (
                <div>
                    <h6 className="citation-section-title">Home Medications</h6>
                    <Table className="citation-table" striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Medication</th>
                                <th>Order Date</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {multipleEntries.map((med, index) => (
                                <tr key={index}>
                                    <td><strong>{med.med_subclass}</strong></td>
                                    <td>{parseAsUTC(med.order_date).toLocaleDateString()}</td>
                                    <td>
                                        <Badge bg="success">
                                            Active
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            );
        }

        return null;
    };

    if (!citationData) {
        return (
            <>
                <Modal show={show} onHide={() => setShow(false)} size="xl" className="citation-modal">
                    <Modal.Header closeButton className="citation-modal-header">
                        <Modal.Title>Citation Not Found</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="text-center text-muted">
                            Citation {citationNumber} could not be found in the data.
                        </div>
                    </Modal.Body>
                </Modal>
            </>
        );
    }

    return (
        <>
            <Modal show={show} onHide={() => setShow(false)} size="lg" className="citation-modal">
                <Modal.Header closeButton className="citation-modal-header">
                    <Modal.Title className="citation-modal-title">
                        <span>Citation Details</span>
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="citation-modal-body">
                    <Card className="citation-source-card">
                        <Card.Body>
                            <h6 className="citation-source-title">Source Information</h6>
                            <div className="citation-source-info">
                                <span><strong>Data Type:</strong> {formatKey(citationData.data_type)}</span>
                                <span><strong>Item Key:</strong> {citationData.item_key}</span>
                            </div>
                            
                            {citationData.metadata && Object.keys(citationData.metadata).length > 0 && (
                                <div className="citation-metadata-container">
                                    <small className="text-muted me-2">Metadata:</small>
                                    {Object.entries(citationData.metadata).map(([key, value]) => (
                                        <Badge key={key} bg="secondary" className="citation-metadata-badge">
                                            {formatKey(key)}: {value}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {Object.keys(citationData.displayData).length > 0 && (
                        <div>
                            <h6 className="citation-section-title">Data Details</h6>
                            <Table className="citation-table" borderless>
                                <tbody>
                                    {Object.entries(citationData.displayData).map(([key, value]) => (
                                        value !== null && value !== undefined && value !== '' && (
                                            <tr key={key} className="citation-table-row">
                                                <td className="citation-table-label">{key}</td>
                                                <td className="citation-table-value">
                                                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                                                </td>
                                            </tr>
                                        )
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Multiple Entries */}
                    {renderMultipleEntries()}

                    <small className="text-muted">
                        <FontAwesomeIcon icon={faCheckCircle} /> Citation via FHIR.
                    </small>
                </Modal.Body>
            </Modal>

            {/* Labs Modal - using unified Labs.tsx component */}
            <LabsModal
                showModal={showLabsModal}
                setShowModal={setShowLabsModal}
                labs={transformedLabs}
                patientVisit={patientVisit}
                dataSource="snapshot"
            />
        </>
    );
};
